import type { ReleaseApprovalMode } from '@src/dto';
import type { ReactNode } from 'react';

import {
  MAX_DAPP_NAME_LENGTH,
  MAX_SUBTITLE_LENGTH,
} from '../../../constants/form';
import * as Form from '../../Form';
import {
  CharacterCountHint,
  charactersLeft,
} from '../../shared/CharacterCountHint';
import type { DappFormInputChangeEvent } from '../types';
import { ReleaseApprovalModeField } from './ReleaseApprovalModeField';

interface BasicDetailsSectionBaseProps {
  dappName: string;
  subtitle: string;
  description: string;
  releaseApprovalMode: ReleaseApprovalMode;
  onInputChange?: (event: DappFormInputChangeEvent) => void;
  packageNameField: ReactNode;
  /** New-dapp only: the "Your Solana Wallet" row rendered before the uploads. */
  walletField?: ReactNode;
  uploadFields: ReactNode;
  previewUploadsField: ReactNode;
}

/** Store Listing / New dApp field list — one flat column, `gap-6` between fields. */
export function BasicDetailsSectionBase({
  dappName,
  subtitle,
  description,
  releaseApprovalMode,
  onInputChange,
  packageNameField,
  walletField,
  uploadFields,
  previewUploadsField,
}: BasicDetailsSectionBaseProps) {
  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Form.Input
          label="App Name"
          name="dappName"
          defaultValue={dappName}
          onChange={onInputChange}
          maxLength={MAX_DAPP_NAME_LENGTH}
          required
        />
        <CharacterCountHint
          className="text-left tabular-nums"
          text={charactersLeft(dappName, MAX_DAPP_NAME_LENGTH)}
        />
      </div>

      {packageNameField}

      <div className="flex flex-col gap-1">
        <Form.Input
          label="Subtitle"
          explanation="This appears beneath your app name in lists and on the detail page."
          name="subtitle"
          defaultValue={subtitle}
          onChange={onInputChange}
          maxLength={MAX_SUBTITLE_LENGTH}
          required
        />
        <CharacterCountHint
          className="text-left tabular-nums"
          text={charactersLeft(subtitle, MAX_SUBTITLE_LENGTH)}
        />
      </div>

      <Form.Textarea
        label="Description"
        explanation="In a short paragraph, describe your app."
        id="description"
        name="description"
        placeholder="Describe your app"
        defaultValue={description}
        onChange={onInputChange}
        rows={4}
        required
      />

      <ReleaseApprovalModeField
        value={releaseApprovalMode}
        onChange={onInputChange}
      />

      {walletField}

      {uploadFields}

      {previewUploadsField}
    </section>
  );
}
